import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { FiscalYearPicker } from '@/components/sales-trackers/FiscalYearPicker'
import { RepSheet } from '@/components/sales-trackers/RepSheet'
import { useBillingsPeriod, periodQuery } from '@/hooks/use-billings-period'
import { useBillingsMyFiscalYears, useBillingsMySheet } from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { explainError } from '@/lib/explain-error'

/**
 * A sales rep's own numbers. Read-only, and only ever theirs.
 *
 * This is a SEPARATE page from By Rep rather than the same one with the controls hidden,
 * and the difference is not cosmetic. By Rep takes the rep from the URL, so hiding a
 * dropdown would leave the identifier sitting in the address bar for anybody to edit.
 * This page has no identifier at all: it reads /billings/me/sheet, which uses the
 * session, and there is nothing in the request to point at a colleague.
 *
 * Everything editable is simply absent rather than disabled: no targets button, no
 * reattribution, no billing lines. That matters because the CRM's impersonation
 * read-only guard does not work - blockMutations is mounted before authenticate sets
 * req.isReadOnly, so it never fires - and a disabled button is a UI opinion, not a
 * control. The server side is GET-only for the same reason.
 */
export default function SalesTrackersMySales() {
  useDocumentTitle('My sales')

  const years = useBillingsMyFiscalYears()
  const period = useBillingsPeriod(years.data)
  const { fy, granularity, month, quarter, setPeriod } = period
  const effectiveFy = fy ?? years.data?.[0]?.fiscal_year
  const query = { fy: effectiveFy, ...periodQuery(period) }
  const sheet = useBillingsMySheet(query)

  // 404 means "you are not a rep", which is a normal answer and not a failure. It is the
  // only case here worth its own copy: an empty dashboard would leave somebody guessing.
  const notARep = (sheet.error as { response?: { status?: number } })?.response?.status === 404

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div>
        <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
          My sales
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your invoiced billings from QuickBooks, and how they track against your targets. Figures
          are CAD and include house accounts.
        </p>
      </div>

      {notARep ? (
        <Alert role="note">
          <AlertTitle>You are not set up as a sales rep</AlertTitle>
          <AlertDescription>
            There is no sales sheet for your account. If that looks wrong, ask an administrator to
            add you on the Sales Trackers admin page.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {years.data && years.data.length > 0 && (
            <FiscalYearPicker
              years={years.data}
              value={effectiveFy}
              onChange={(next) => setPeriod({ fy: next })}
              period={{ granularity, month, quarter, onChange: setPeriod }}
            />
          )}

          <p role="status" aria-live="polite" className="sr-only">
            {sheet.isFetching ? 'Loading your sales' : sheet.data ? 'Your sales loaded' : ''}
          </p>

          {sheet.isError && !notARep && (
            <Alert variant="destructive">
              <AlertTitle>Could not load your sales</AlertTitle>
              <AlertDescription>
                {explainError(sheet.error, 'Something went wrong loading your sales.')}
              </AlertDescription>
            </Alert>
          )}

          {/* No onEditTargets and no lines: a rep views, nobody edits. */}
          <RepSheet selfService query={query} showLines={false} />
        </>
      )}
    </section>
  )
}
